import InfoOutlined from "@mui/icons-material/InfoOutlined";
import TrendingDown from "@mui/icons-material/TrendingDown";
import TrendingFlat from "@mui/icons-material/TrendingFlat";
import TrendingUp from "@mui/icons-material/TrendingUp";
import { Alert, Box, Tooltip, Typography } from "@mui/material";
import type { KeyboardEvent, SyntheticEvent } from "react";
import { useTranslation } from "react-i18next";
import { KPI_CARD_FONT_SIZE_REM, KPI_CARD_HEIGHT_PX } from "@/constants";
import type { AppKpiDisplayConfig, AppKpiFormat } from "@/features/app-studio/api/appKpisApi";
import {
  comparisonPeriodsOf,
  comparisonPeriodTexts,
  type AppComparisonPeriods,
} from "@/features/app-studio/lib/comparisonDisplay";
import {
  kpiFontSizeOf,
  resolveKpiCardColors,
  resolveKpiChange,
  type KpiChangeDirection,
  type KpiChangeDisplay,
} from "@/features/app-studio/lib/kpiCardStyle";

type AppKpiCardViewProps = {
  name: string;
  /**
   * What the metric means. When present, an info icon follows the title and its tooltip shows the
   * title and this description together.
   */
  description?: string | null;
  format: AppKpiFormat;
  /** Colours, font sizes and value-based colour rules; absent or null is the default look. */
  displayConfig?: AppKpiDisplayConfig | null;
  value?: unknown;
  /** The change chip's raw number, from `displayConfig.change.column` of the same row. */
  changeValue?: unknown;
  /** The periods a comparison query compared; a chip with no label of its own is dated by them. */
  comparisonPeriods?: AppComparisonPeriods | null;
  error?: string | null;
  /**
   * Render the label and value at a larger type scale — used in the
   * published app view, where the KPI card is the primary content shown to
   * end users rather than a compact builder-panel tile.
   */
  largeContent?: boolean;
  /**
   * Fixed card height override (px). Defaults to KPI_CARD_HEIGHT_PX (the
   * app-studio builder sizing). The published app view passes
   * PUBLISHED_KPI_CARD_HEIGHT_PX instead, since its chart/table cards carry
   * an extra summary block that the builder's don't.
   */
  heightPx?: number;
  /**
   * Open this card's drill-down widget. When set the whole card becomes a button — pointer cursor
   * and an accent border on hover. Only passed when the card actually has a resolvable drill-down
   * widget. No corner icon: `OpenInFull` is a chart/table's real expand action elsewhere in the
   * app, so on a card that opens a *different* widget it read as "make this bigger".
   */
  onDrilldown?: () => void;
  /** Accessible name for the drill-down action; required whenever `onDrilldown` is set. */
  drilldownAriaLabel?: string;
};

function formatKpiValue(value: unknown, format: AppKpiFormat): string {
  if (value === null || value === undefined) {
    return "—";
  }
  if (format === "text") {
    return String(value);
  }

  const numeric = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(numeric)) {
    return String(value);
  }

  switch (format) {
    case "currency":
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(numeric);
    case "percent":
      return `${new Intl.NumberFormat().format(numeric)}%`;
    case "number":
    default:
      return new Intl.NumberFormat().format(numeric);
  }
}

/** The theme's colour for a direction no colour was picked for. */
const CHANGE_DEFAULT_COLOR: Record<KpiChangeDirection, string> = {
  up: "var(--theme-success)",
  down: "var(--theme-error)",
  flat: "var(--theme-text-secondary)",
};

const CHANGE_ICON: Record<KpiChangeDirection, typeof TrendingUp> = {
  up: TrendingUp,
  down: TrendingDown,
  flat: TrendingFlat,
};

const CHANGE_SIGN: Record<KpiChangeDirection, string> = {
  up: "+",
  down: "-",
  flat: "",
};

function formatKpiChange(change: KpiChangeDisplay, format: AppKpiFormat): string {
  const magnitude = change.isPercent
    ? `${new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(change.magnitude)}%`
    : formatKpiValue(change.magnitude, format);
  return `${CHANGE_SIGN[change.direction]}${magnitude}`;
}

function KpiChangeChip({
  change,
  format,
  label,
  labelColor,
}: {
  change: KpiChangeDisplay;
  format: AppKpiFormat;
  /** The text after the number: the chip's own label, or the compared period's dates. */
  label: string | null;
  /** The text after the number takes the card's name colour, so a set text colour covers it. */
  labelColor: string;
}) {
  const color = change.color ?? CHANGE_DEFAULT_COLOR[change.direction];
  const Icon = CHANGE_ICON[change.direction];
  const fontSizeRem = KPI_CARD_FONT_SIZE_REM.CHANGE[change.size];
  return (
    <Box
      data-testid="kpi-change-chip"
      sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 0.75, mt: 1 }}
    >
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          px: 1,
          py: 0.25,
          borderRadius: 999,
          color,
          bgcolor: `color-mix(in srgb, ${color} 12%, transparent)`,
          border: `1px solid color-mix(in srgb, ${color} 35%, transparent)`,
          fontSize: `${fontSizeRem}rem`,
          fontWeight: 700,
          whiteSpace: "nowrap",
        }}
      >
        <Icon sx={{ fontSize: `${(fontSizeRem * 4) / 3}rem` }} aria-hidden />
        {formatKpiChange(change, format)}
      </Box>
      {label ? (
        <Typography
          variant="caption"
          noWrap
          sx={{ minWidth: 0, color: labelColor, fontSize: `${fontSizeRem}rem` }}
        >
          {label}
        </Typography>
      ) : null}
    </Box>
  );
}

/** Keeps a click or key on the info icon from also opening the card's drill-down. */
function stopPropagation(event: SyntheticEvent) {
  event.stopPropagation();
}

export function AppKpiCardView({
  name,
  description = null,
  format,
  displayConfig = null,
  value,
  changeValue,
  comparisonPeriods = null,
  error,
  largeContent = false,
  heightPx = KPI_CARD_HEIGHT_PX,
  onDrilldown,
  drilldownAriaLabel,
}: AppKpiCardViewProps) {
  const { t } = useTranslation("appStudio");
  // An errored KPI renders as an alert rather than a card, so it is never a drill-down source
  // even when one is configured — there is no card body to click.
  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 1 }}>
        {error}
      </Alert>
    );
  }

  const formattedValue = formatKpiValue(value, format);
  const isDrillable = onDrilldown !== undefined;
  const colors = resolveKpiCardColors(displayConfig, value, format);
  const change = resolveKpiChange(displayConfig, value, changeValue, format);
  // A blank label follows the tab's date filter: it names whatever period the chip compared with.
  const periods = comparisonPeriodsOf(comparisonPeriods);
  const changeLabel =
    change?.label ??
    (periods === null
      ? null
      : t("comparisonPeriods.kpiChipLabel", {
          range: comparisonPeriodTexts(t, periods).previous,
        }));
  const scale = largeContent ? "LARGE" : "COMPACT";
  // Only a size that was set overrides the variant, so an unstyled card renders exactly as before.
  const valueFontSize = displayConfig?.value_font_size
    ? `${KPI_CARD_FONT_SIZE_REM.VALUE[scale][kpiFontSizeOf(displayConfig.value_font_size)]}rem`
    : undefined;
  const labelFontSize = displayConfig?.label_font_size
    ? `${KPI_CARD_FONT_SIZE_REM.LABEL[scale][kpiFontSizeOf(displayConfig.label_font_size)]}rem`
    : undefined;
  const labelColor = colors.textColor ?? "var(--theme-text-secondary)";

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }
    // Space would otherwise scroll the page while the card has focus.
    event.preventDefault();
    onDrilldown?.();
  };

  return (
    <Box
      data-testid="kpi-card"
      role={isDrillable ? "button" : undefined}
      tabIndex={isDrillable ? 0 : undefined}
      aria-label={isDrillable ? drilldownAriaLabel : undefined}
      onClick={isDrillable ? onDrilldown : undefined}
      onKeyDown={isDrillable ? handleKeyDown : undefined}
      sx={{
        height: `${heightPx}px`,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        textAlign: "center",
        px: 2,
        py: 2,
        minWidth: 0,
        borderRadius: 2,
        border: "1px solid var(--theme-border)",
        bgcolor: colors.cardColor ?? "var(--theme-surface)",
        ...(isDrillable
          ? {
              cursor: "pointer",
              transition: "border-color 120ms ease-in-out",
              "&:hover": { borderColor: "var(--theme-accent)" },
              "&:focus-visible": {
                outline: "2px solid var(--theme-accent)",
                outlineOffset: 2,
              },
            }
          : {}),
      }}
    >
      <Tooltip title={formattedValue} followCursor arrow>
        <Typography
          variant={largeContent ? "h3" : "h4"}
          noWrap
          sx={{
            fontWeight: 700,
            color: colors.textColor ?? "var(--theme-text-primary)",
            ...(valueFontSize ? { fontSize: valueFontSize } : {}),
          }}
        >
          {formattedValue}
        </Typography>
      </Tooltip>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.5,
          minWidth: 0,
        }}
      >
        <Tooltip title={name} followCursor arrow>
          <Typography
            variant={largeContent ? "h6" : "body2"}
            noWrap
            sx={{
              minWidth: 0,
              color: labelColor,
              fontWeight: largeContent ? 600 : 400,
              ...(labelFontSize ? { fontSize: labelFontSize } : {}),
            }}
          >
            {name}
          </Typography>
        </Tooltip>
        {description ? (
          <Tooltip
            arrow
            placement="top"
            title={
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {name}
                </Typography>
                <Typography variant="body2">{description}</Typography>
              </Box>
            }
          >
            <Box
              component="span"
              role="img"
              tabIndex={0}
              aria-label={description}
              onClick={stopPropagation}
              onKeyDown={stopPropagation}
              sx={{
                display: "inline-flex",
                flexShrink: 0,
                color: labelColor,
                "&:focus-visible": {
                  outline: "2px solid var(--theme-accent)",
                  borderRadius: "50%",
                },
              }}
            >
              <InfoOutlined fontSize="small" />
            </Box>
          </Tooltip>
        ) : null}
      </Box>
      {change !== null ? (
        <KpiChangeChip
          change={change}
          format={format}
          label={changeLabel}
          labelColor={labelColor}
        />
      ) : null}
    </Box>
  );
}
